<template>
  <div class="page-layout">
    <Sidebar :class="{ open: isSidebarOpen }" @toggle-sidebar="toggleSidebar" />
    <div class="addon-details-container">
      <div class="header">
        <div class="header-left">
          <button class="menu-toggle" @click="toggleSidebar" aria-label="Toggle Menu">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 12h18M3 6h18M3 18h18"/>
            </svg>
          </button>
          <h1>{{ isEditing ? 'Edit Addon' : (addon ? addon.name : 'New Addon') }}</h1>
        </div>
        <div class="header-actions">
          <router-link to="/addons" class="back-btn">Back to List</router-link>
        </div>
      </div>

      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>{{ loadingMessage }}</p>
      </div>
      
      <div v-else-if="error" class="error-container">
        <p class="error-message">{{ error }}</p>
        <router-link to="/addons" class="back-btn">Back to List</router-link>
      </div>
      
      <form v-else @submit.prevent="saveAddon" class="addon-form">
        <div class="form-group">
          <label for="name">Name</label>
          <input 
            id="name" 
            v-model="formData.name" 
            type="text" 
            required
            placeholder="Enter addon name"
          >
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea 
            id="description" 
            v-model="formData.description" 
            required
            placeholder="Enter addon description"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="price">Price</label>
          <input 
            id="price" 
            v-model="formData.price" 
            type="number" 
            step="0.01" 
            required
            placeholder="Enter price"
          >
        </div>

        <div v-if="addon" class="status-toggle">
          <label>Status</label>
          <button 
            type="button"
            @click="toggleStatus"
            :class="['toggle-btn', addon.is_active ? 'active' : 'inactive']"
          >
            {{ addon.is_active ? 'Active' : 'Inactive' }}
          </button>
        </div>

        <div class="form-actions">
          <button type="submit" class="save-btn">
            {{ isEditing ? 'Update' : 'Create' }} Addon
          </button>
          <button 
            v-if="addon" 
            type="button" 
            @click="confirmDelete" 
            class="delete-btn"
          >
            Delete Addon
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'AddonDetails',
  components: {
    Sidebar
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const addon = ref(null);
    const loading = ref(true);
    const loadingMessage = ref('Loading...');
    const error = ref(null);
    const isSidebarOpen = ref(false);
    const formData = ref({
      name: '',
      description: '',
      price: ''
    });

    const isEditing = computed(() => !!route.params.id);

    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value;
    };

    const fetchAddon = async () => {
      if (!route.params.id) {
        loading.value = false;
        return;
      }

      try {
        loading.value = true;
        loadingMessage.value = 'Loading addon details...';
        error.value = null;
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get(`https://api.kewaar.com/api/admin/addons/show/${route.params.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          addon.value = response.data.data;
          formData.value = {
            name: addon.value.name,
            description: addon.value.description,
            price: addon.value.price
          };
        } else {
          throw new Error(response.data.message || 'Failed to load addon');
        }
      } catch (err) {
        error.value = err.message || 'An error occurred while loading the addon';
        console.error('Error fetching addon:', err);
      } finally {
        loading.value = false;
      }
    };

    const saveAddon = async () => {
      try {
        loading.value = true;
        loadingMessage.value = isEditing.value ? 'Updating addon...' : 'Creating addon...';
        error.value = null;

        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        if (isEditing.value) {
          await axios.put(
            `https://api.kewaar.com/api/admin/addons/${route.params.id}`,
            formData.value,
            { headers }
          );
        } else {
          await axios.post(
            'https://api.kewaar.com/api/admin/addons',
            formData.value,
            { headers }
          );
        }

        router.push('/addons');
      } catch (err) {
        error.value = err.message || 'An error occurred while saving the addon';
        console.error('Error saving addon:', err);
      } finally {
        loading.value = false;
      }
    };

    const toggleStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `https://api.kewaar.com/api/admin/addons/${route.params.id}/status`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        addon.value.is_active = !addon.value.is_active;
      } catch (error) {
        console.error('Error toggling status:', error);
      }
    };

    const confirmDelete = async () => {
      if (confirm('Are you sure you want to delete this addon?')) {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(
            `https://api.kewaar.com/api/admin/addons/${route.params.id}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          router.push('/addons');
        } catch (error) {
          console.error('Error deleting addon:', error);
        }
      }
    };

    onMounted(fetchAddon);

    return {
      addon,
      loading,
      loadingMessage,
      error,
      formData,
      isEditing,
      isSidebarOpen,
      toggleSidebar,
      saveAddon,
      toggleStatus,
      confirmDelete
    };
  }
};
</script>

<style scoped>
.page-layout {
  display: flex;
  min-height: 100vh;
  width: 100%;
}

.addon-details-container {
  flex: 1;
  padding: 2rem;
  min-width: 0;
  width: 100%;
  margin-left: 280px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header h1 {
  margin: 0;
  font-size: 1.8rem;
  color: #2c3e50;
}

.menu-toggle {
  display: none;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #2c3e50;
}

.menu-toggle svg {
  width: 24px;
  height: 24px;
}

.back-btn {
  padding: 0.5rem 1rem;
  background-color: #6c757d;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background-color: #5a6268;
}

.addon-form {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.status-toggle {
  margin-bottom: 1.5rem;
}

.toggle-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
}

.toggle-btn.active {
  background-color: #e3f2fd;
  color: #1976d2;
}

.toggle-btn.inactive {
  background-color: #fce4ec;
  color: #c2185b;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.save-btn:hover {
  background-color: #45a049;
}

.delete-btn {
  padding: 0.75rem 1.5rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.delete-btn:hover {
  background-color: #c82333;
}

@media (max-width: 768px) {
  .addon-details-container {
    margin-left: 0;
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .menu-toggle {
    display: block;
  }

  .addon-form {
    padding: 1rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions button {
    width: 100%;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  text-align: center;
  padding: 2rem;
}

.error-message {
  color: #dc3545;
  margin-bottom: 1rem;
}
</style> 